import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { customInstance } from "config/mutator";
import type { TouchpointBatchBaseDTO } from "generated/schemas";

import { touchpointKeys } from "hooks/api/query-keys";

type UseLatestBatchParams = {
  clientId: string;
  campaignId?: string;
  touchpointId?: string;
  enabled?: boolean;
};

const fetchLatestBatch = async ({
  clientId,
  campaignId,
  touchpointId,
}: Required<Pick<UseLatestBatchParams, "clientId" | "campaignId" | "touchpointId">>) => {
  const data = await customInstance<TouchpointBatchBaseDTO>({
    url: `/api/clients/${clientId}/campaigns/${campaignId}/touchpoints/${touchpointId}/latest-batch`,
    method: "GET",
  });

  return data?.id ? data : null;
};

export const useLatestBatch = <TData = TouchpointBatchBaseDTO | null>(
  params: UseLatestBatchParams,
  options?: Omit<
    UseQueryOptions<TouchpointBatchBaseDTO | null, Error, TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const { clientId, campaignId, touchpointId, enabled = true } = params;

  const isQueryEnabled =
    Boolean(clientId) &&
    Boolean(campaignId) &&
    Boolean(touchpointId) &&
    enabled &&
    (options?.enabled ?? true);

  return useQuery({
    queryKey: touchpointKeys.latestBatch(clientId, campaignId ?? "", touchpointId ?? ""),
    queryFn: async () => {
      if (!campaignId || !touchpointId) {
        throw new Error("campaignId and touchpointId are required for useLatestBatch queryFn");
      }

      return fetchLatestBatch({ clientId, campaignId, touchpointId });
    },
    // Opts out of the global staleTime: this only runs while the Add-to-batch modal is open, so
    // reopening it must show the batch as it is now, not as it was when the modal last closed.
    staleTime: 0,
    ...options,
    enabled: isQueryEnabled,
  });
};
